/**
 * Defensive guard for the React × browser-translation DOM crash
 * (PLATAFORMA-N; same class as CHAT-MARITACA-27/28, facebook/react#11538).
 *
 * Google Translate / Chrome's built-in page translation replaces the text
 * nodes React rendered with <font> wrappers, re-parenting them. React still
 * holds references to the original nodes, so on its next commit it calls
 * removeChild()/insertBefore() on a node translation has since moved, and the
 * browser throws:
 *
 *   NotFoundError: Failed to execute 'removeChild'  on 'Node': ...
 *   NotFoundError: Failed to execute 'insertBefore' on 'Node': ...
 *
 * On /recarga this fires the moment the user clicks "Confirmar pagamento":
 * the button swaps its icon (Wallet → Loader2) in front of a text node that
 * translation has moved, react-dom's insertBefore throws, the whole tree
 * (Stripe Payment Element included) is torn down, and the in-flight
 * stripe.confirmPayment() rejects with "elements should have a mounted
 * Payment Element". Every one of the 39 PLATAFORMA-N events carried the
 * translation fingerprints (<font> click targets + this NotFoundError) —
 * remounting the element (#1147/#1283) could not help because the tree that
 * would host it was already gone.
 *
 * Marking the React root translate="no" (public/index.html) is only a hint;
 * Chrome ignores it when the user translates the entire page. This patch makes
 * the two mutation methods resilient: when the target node is no longer under
 * the expected parent, fall back to its real parent (removeChild) or append
 * (insertBefore) instead of throwing, so React's reconciliation survives.
 *
 * Mirrors frontend/src/googleTranslateGuard.ts (#872). Idempotent and a no-op
 * outside a DOM environment.
 */
export function installGoogleTranslateGuard(): void {
  if (typeof Node !== "function" || !Node.prototype) return;

  const proto = Node.prototype as Node & { __translateGuardInstalled?: boolean };
  if (proto.__translateGuardInstalled) return;
  proto.__translateGuardInstalled = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      // Translation re-parented `child`; remove it from where it actually is
      // (or no-op if it is already detached) rather than throwing.
      if (child.parentNode) {
        return originalRemoveChild.call(child.parentNode, child) as T;
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // The reference node was re-parented by translation; append to `this`
      // instead of throwing.
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
